function isRecord(value) {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}
function toBoolean(value) {
    return value === true;
}
export function sanitizePlainText(value, maxLength = 10000) {
    const raw = typeof value === 'string' ? value : String(value ?? '');
    const withoutControl = raw
        .slice(0, maxLength)
        // eslint-disable-next-line no-control-regex -- stripping control characters is the point of this pattern
        .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/g, '');
    return withoutControl.trim();
}
export function isSafeUrl(value) {
    if (typeof value !== 'string')
        return false;
    const trimmed = value.trim();
    if (trimmed === '' || trimmed.length > 2000)
        return false;
    // eslint-disable-next-line no-control-regex -- stripping control characters is the point of this pattern
    if (/[\u0000-\u001F\u007F]/.test(trimmed))
        return false;
    const schemeMatch = /^([a-zA-Z][a-zA-Z0-9+.-]*):/.exec(trimmed);
    if (!schemeMatch)
        return false;
    const scheme = (schemeMatch[1] ?? '').toLowerCase();
    if (scheme === 'http' || scheme === 'https') {
        try {
            const url = new URL(trimmed);
            return url.protocol === `${scheme}:`;
        }
        catch {
            return false;
        }
    }
    if (scheme === 'mailto') {
        return /^mailto:[^,;\s]+@[^,;\s]+$/i.test(trimmed);
    }
    if (scheme === 'tel') {
        return /^tel:[\d+() -]+$/i.test(trimmed);
    }
    return false;
}
export function sanitizeUrl(value, opts) {
    if (typeof value !== 'string')
        return '';
    const trimmed = value.trim().slice(0, 2000);
    if (trimmed === '')
        return '';
    const allowRelative = opts?.allowRelative === true;
    if (allowRelative) {
        if (trimmed.startsWith('#') && /^#[A-Za-z0-9:_-]+$/.test(trimmed)) {
            return trimmed;
        }
        if (trimmed.startsWith('/') &&
            !trimmed.startsWith('//') &&
            !/\s/.test(trimmed)) {
            return trimmed;
        }
    }
    return isSafeUrl(trimmed) ? trimmed : '';
}
export function sanitizeCssColor(value) {
    if (typeof value !== 'string')
        return null;
    const trimmed = value.trim();
    if (!/^#(?:[0-9a-f]{3}|[0-9a-f]{4}|[0-9a-f]{6}|[0-9a-f]{8})$/i.test(trimmed)) {
        return null;
    }
    return trimmed.toLowerCase();
}
export function sanitizeFontFamily(value) {
    if (typeof value !== 'string' || value.trim() === '')
        return 'sans-serif';
    const cleaned = value.replace(/[{}<>;`]/g, '').replace(/\\(?!\s)/g, '');
    const parts = cleaned
        .split(',')
        .map((part) => part.trim())
        .filter(Boolean)
        .slice(0, 4);
    if (parts.length === 0)
        return 'sans-serif';
    const generics = new Set([
        'sans-serif',
        'serif',
        'monospace',
        'cursive',
        'fantasy',
        'system-ui',
        'ui-sans-serif',
        'ui-serif',
        'ui-monospace',
        'ui-rounded'
    ]);
    const safe = parts
        .map((part) => {
        const lower = part.toLowerCase();
        if (generics.has(lower))
            return part;
        if (/^"[^"]+"$/.test(part) || /^'[^']+'$/.test(part)) {
            return `"${part.slice(1, -1).replace(/"/g, '\\"')}"`;
        }
        if (/^[A-Za-z0-9][A-Za-z0-9 -]*$/.test(part)) {
            return `"${part}"`;
        }
        return null;
    })
        .filter((part) => part !== null);
    return safe.length > 0 ? safe.join(', ') : 'sans-serif';
}
function sanitizeLinkValue(value) {
    if (typeof value !== 'string')
        return undefined;
    const clean = sanitizeUrl(value, { allowRelative: true });
    return clean || undefined;
}
function sanitizeInlineArray(value) {
    if (!Array.isArray(value))
        return [];
    const out = [];
    for (const raw of value.slice(0, 2000)) {
        if (!isRecord(raw))
            continue;
        if (raw.kind === 'br') {
            out.push({ kind: 'br' });
            continue;
        }
        if (raw.kind !== 'text')
            continue;
        const text = sanitizePlainText(raw.text, 5000);
        if (text === '')
            continue;
        const node = { kind: 'text', text };
        if (toBoolean(raw.bold))
            node.bold = true;
        if (toBoolean(raw.italic))
            node.italic = true;
        const link = sanitizeLinkValue(raw.link);
        if (link)
            node.link = link;
        out.push(node);
    }
    return out;
}
export function sanitizeRichText(value) {
    if (!Array.isArray(value))
        return [];
    const out = [];
    for (const raw of value.slice(0, 2000)) {
        if (!isRecord(raw))
            continue;
        if (raw.kind === 'p') {
            const paragraph = {
                kind: 'p',
                children: sanitizeInlineArray(raw.children)
            };
            out.push(paragraph);
            continue;
        }
        if (raw.kind === 'ul' || raw.kind === 'ol') {
            if (!Array.isArray(raw.items))
                continue;
            const items = raw.items.slice(0, 500).map((item) => sanitizeInlineArray(item));
            const list = { kind: raw.kind, items };
            out.push(list);
        }
    }
    return out;
}
//# sourceMappingURL=sanitize.js.map